import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Upload, X, FileImage, Loader2, Check, ChefHat,
  Trash2, Plus, ArrowRight, Sparkles, Camera, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MagicMenuPage() {
  const { token, activeBranch } = useAuth();
  const { isAr, t } = useLanguage();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | extracting | review | saving | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [items, setItems] = useState([]);
  const [restaurantName, setRestaurantName] = useState('');
  const [currency, setCurrency] = useState('ر.س');
  const [saving, setSaving] = useState(false);
  const [addedCount, setAddedCount] = useState(0);

  const l = useCallback((ar, en) => isAr ? ar : en, [isAr]);

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowed.includes(f.type)) {
      toast.error(l('نوع الملف غير مدعوم. يرجى رفع صورة أو PDF.', 'Unsupported file type.'));
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error(l('الملف كبير جداً. الحد الأقصى 10 ميقابايت.', 'File too large. Max 10MB.'));
      return;
    }
    setFile(f);
    if (f.type.startsWith('image/')) {
      setPreview(URL.createObjectURL(f));
    } else {
      setPreview(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  }, []);

  const extract = async () => {
    if (!file || !activeBranch) return;
    setStep('extracting');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/api/copilot/magic-menu/extract`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (data.success && data.items?.length > 0) {
        setItems(data.items.map((it, i) => ({ ...it, _selected: true, _key: i })));
        setRestaurantName(data.restaurant_name || '');
        setCurrency(data.currency || 'ر.س');
        setStep('review');
        toast.success(l(`تم استخراج ${data.items.length} صنف!`, `Extracted ${data.items.length} items!`));
      } else {
        toast.error(l('لم يتم العثور على أصناف في الصورة.', 'No items found.'));
        setStep('upload');
      }
    } catch (err) {
      toast.error(l('فشل الاستخراج. حاول بصورة أوضح.', 'Extraction failed. Try a clearer image.'));
      setStep('upload');
    }
  };

  const toggleItem = (key) => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, _selected: !it._selected } : it));
  };

  const updateItem = (key, field, value) => {
    setItems(prev => prev.map(it => it._key === key ? { ...it, [field]: value } : it));
  };

  const removeItem = (key) => {
    setItems(prev => prev.filter(it => it._key !== key));
  };

  const confirm = async () => {
    const selected = items.filter(it => it._selected);
    if (selected.length === 0) {
      toast.error(l('اختر صنف واحد على الأقل.', 'Select at least one item.'));
      return;
    }
    setSaving(true);
    setStep('saving');
    try {
      const payload = {
        branch_id: activeBranch.id,
        items: selected.map(({ _selected, _key, ...rest }) => rest),
      };
      const { data } = await axios.post(`${API}/api/copilot/magic-menu/confirm`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setAddedCount(data.added_count || 0);
      setStep('done');
      toast.success(l(`تم إضافة ${data.added_count} صنف للمنيو!`, `Added ${data.added_count} items!`));
    } catch {
      toast.error(l('فشل الحفظ. حاول مرة أخرى.', 'Save failed. Try again.'));
      setStep('review');
    }
    setSaving(false);
  };

  const selectedCount = items.filter(it => it._selected).length;
  const primaryColor = '#D97706';

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto" dir={isAr ? 'rtl' : 'ltr'} style={{ fontFamily: "'Tajawal', sans-serif" }}>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg, ${primaryColor}, #F59E0B)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 data-testid="magic-menu-title" className="text-xl font-bold text-foreground">{l('الإعداد السحري', 'Magic Setup')}</h1>
            <p className="text-sm text-muted-foreground">{l('ارفع صورة المنيو الورقي وسنستخرج كل الأصناف تلقائياً', 'Upload your paper menu and we extract items automatically')}</p>
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center gap-2 mt-4">
          {[
            { id: 'upload', ar: 'رفع', en: 'Upload' },
            { id: 'review', ar: 'مراجعة', en: 'Review' },
            { id: 'done', ar: 'تم', en: 'Done' },
          ].map((s, i) => {
            const isActive = step === s.id || (step === 'extracting' && s.id === 'upload') || (step === 'saving' && s.id === 'review');
            const isDone = (s.id === 'upload' && ['review', 'saving', 'done'].includes(step)) || (s.id === 'review' && step === 'done');
            return (
              <div key={s.id} className="flex items-center gap-2">
                {i > 0 && <div style={{ width: 32, height: 2, background: isDone ? primaryColor : 'rgba(255,255,255,0.08)', borderRadius: 1 }} />}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 20,
                  background: isDone ? `${primaryColor}15` : isActive ? `${primaryColor}10` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isDone ? `${primaryColor}30` : isActive ? `${primaryColor}20` : 'rgba(255,255,255,0.06)'}`,
                }}>
                  {isDone ? <Check className="w-3 h-3" style={{ color: primaryColor }} /> : null}
                  <span style={{ fontSize: 11, fontWeight: isDone || isActive ? 600 : 400, color: isDone ? primaryColor : isActive ? '#F5F5F5' : 'rgba(255,255,255,0.35)' }}>
                    {l(s.ar, s.en)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== UPLOAD STEP ===== */}
      {(step === 'upload' || step === 'extracting') && (
        <div className="space-y-4">
          <div
            data-testid="magic-menu-dropzone"
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !file && fileRef.current?.click()}
            style={{
              border: `2px dashed ${file ? primaryColor : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 16,
              padding: file ? 0 : '48px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: file ? 'default' : 'pointer',
              background: file ? 'transparent' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
              minHeight: file ? 0 : 260,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {!file ? (
              <>
                <div style={{ width: 64, height: 64, borderRadius: 16, background: `${primaryColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <Camera style={{ width: 28, height: 28, color: primaryColor }} />
                </div>
                <p style={{ fontSize: 15, fontWeight: 600, color: '#F5F5F5', marginBottom: 4 }}>{l('اسحب الصورة هنا أو اضغط للرفع', 'Drag image here or click to upload')}</p>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>{l('يدعم: صور (JPEG, PNG, WebP) أو PDF', 'Supports: Images (JPEG, PNG, WebP) or PDF')}</p>
                <div style={{ display: 'flex', gap: 6, marginTop: 16 }}>
                  {[
                    { icon: FileImage, label: l('صورة', 'Image') },
                    { icon: FileText, label: 'PDF' },
                    { icon: Camera, label: l('كاميرا', 'Camera') },
                  ].map((t, i) => (
                    <span key={i} style={{ fontSize: 10, padding: '3px 10px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <t.icon style={{ width: 10, height: 10 }} /> {t.label}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <div className="w-full">
                {preview && (
                  <div style={{ position: 'relative' }}>
                    <img src={preview} alt="menu" style={{ width: '100%', maxHeight: 350, objectFit: 'contain', borderRadius: 14 }} />
                    <button
                      data-testid="magic-menu-remove-file"
                      onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); }}
                      style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: 10, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X style={{ width: 16, height: 16, color: '#fff' }} />
                    </button>
                  </div>
                )}
                {!preview && (
                  <div className="flex items-center gap-3 p-4">
                    <FileText style={{ width: 24, height: 24, color: primaryColor }} />
                    <div className="flex-1">
                      <p style={{ fontSize: 13, fontWeight: 600, color: '#F5F5F5' }}>{file.name}</p>
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{(file.size / 1024).toFixed(0)} KB</p>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setFile(null); }} style={{ color: 'rgba(255,255,255,0.3)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <X style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />

          {file && (
            <Button
              data-testid="magic-menu-extract-btn"
              onClick={extract}
              disabled={step === 'extracting'}
              className="w-full h-12 rounded-xl text-sm font-bold gap-2"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, #F59E0B)`, border: 'none' }}
            >
              {step === 'extracting' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {l('جاري استخراج الأصناف...', 'Extracting items...')}</>
              ) : (
                <><Sparkles className="w-4 h-4" /> {l('استخراج الأصناف بالذكاء الاصطناعي', 'Extract Items with AI')}</>
              )}
            </Button>
          )}
        </div>
      )}

      {/* ===== REVIEW STEP ===== */}
      {(step === 'review' || step === 'saving') && (
        <div className="space-y-4">
          {/* Summary bar */}
          <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <ChefHat className="w-4 h-4" style={{ color: primaryColor }} />
              <span className="text-sm font-semibold text-foreground">
                {restaurantName && <span style={{ color: primaryColor }}>{restaurantName} - </span>}
                {l(`${items.length} صنف تم استخراجه`, `${items.length} items extracted`)}
              </span>
            </div>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {selectedCount} {l('محدد', 'selected')}
            </span>
          </div>

          {/* Items List */}
          <div className="space-y-2">
            {items.map(item => (
              <div
                key={item._key}
                data-testid={`magic-item-${item._key}`}
                className="flex items-center gap-3 p-3 rounded-xl transition-all"
                style={{
                  background: item._selected ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.01)',
                  border: `1px solid ${item._selected ? `${primaryColor}20` : 'rgba(255,255,255,0.04)'}`,
                  opacity: item._selected ? 1 : 0.5,
                }}
              >
                {/* Checkbox */}
                <button
                  data-testid={`magic-item-toggle-${item._key}`}
                  onClick={() => toggleItem(item._key)}
                  style={{
                    width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    background: item._selected ? primaryColor : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${item._selected ? primaryColor : 'rgba(255,255,255,0.1)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  {item._selected && <Check style={{ width: 12, height: 12, color: '#fff' }} />}
                </button>

                {/* Item details */}
                <div className="flex-1 min-w-0">
                  <input
                    data-testid={`magic-item-name-${item._key}`}
                    value={item.name_ar}
                    onChange={e => updateItem(item._key, 'name_ar', e.target.value)}
                    className="w-full bg-transparent text-sm font-semibold text-foreground outline-none"
                    style={{ fontFamily: 'inherit' }}
                  />
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)', padding: '1px 8px', borderRadius: 6 }}>
                      {item.category || l('أخرى', 'Other')}
                    </span>
                    {item.description_ar && (
                      <span className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.25)', maxWidth: 200 }}>{item.description_ar}</span>
                    )}
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-center gap-1">
                  <input
                    data-testid={`magic-item-price-${item._key}`}
                    type="number"
                    value={item.price || ''}
                    onChange={e => updateItem(item._key, 'price', parseFloat(e.target.value) || 0)}
                    className="w-16 text-left bg-transparent text-sm font-bold outline-none"
                    style={{ color: primaryColor, fontFamily: 'inherit', direction: 'ltr', textAlign: 'center' }}
                  />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{currency}</span>
                </div>

                {/* Remove */}
                <button
                  data-testid={`magic-item-remove-${item._key}`}
                  onClick={() => removeItem(item._key)}
                  style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >
                  <Trash2 style={{ width: 12, height: 12, color: '#EF4444' }} />
                </button>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setStep('upload'); setItems([]); setFile(null); setPreview(null); }}
              className="flex-1 h-11 rounded-xl text-sm"
              data-testid="magic-menu-back-btn"
            >
              {l('رفع صورة أخرى', 'Upload Another')}
            </Button>
            <Button
              data-testid="magic-menu-confirm-btn"
              onClick={confirm}
              disabled={saving || selectedCount === 0}
              className="flex-1 h-11 rounded-xl text-sm font-bold gap-2"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, #F59E0B)`, border: 'none' }}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {l('جاري الحفظ...', 'Saving...')}</>
              ) : (
                <><Plus className="w-4 h-4" /> {l(`إضافة ${selectedCount} صنف للمنيو`, `Add ${selectedCount} items`)}</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ===== DONE STEP ===== */}
      {step === 'done' && (
        <div className="text-center py-12">
          <div style={{ width: 72, height: 72, borderRadius: 20, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Check style={{ width: 36, height: 36, color: '#10B981' }} />
          </div>
          <h2 data-testid="magic-menu-success" className="text-lg font-bold text-foreground mb-2">
            {l('تم بنجاح!', 'Success!')}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            {l(`تم إضافة ${addedCount} صنف جديد لمنيو مطعمك.`, `Added ${addedCount} new items to your menu.`)}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => { setStep('upload'); setFile(null); setPreview(null); setItems([]); }}
              className="h-10 rounded-xl text-sm gap-2"
              data-testid="magic-menu-upload-more"
            >
              <Upload className="w-3.5 h-3.5" /> {l('رفع منيو آخر', 'Upload Another')}
            </Button>
            <Button
              onClick={() => navigate('/dashboard/menu')}
              className="h-10 rounded-xl text-sm font-bold gap-2"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, #F59E0B)`, border: 'none' }}
              data-testid="magic-menu-go-to-menu"
            >
              {l('عرض المنيو', 'View Menu')} <ArrowRight className="w-3.5 h-3.5" style={{ transform: isAr ? 'scaleX(-1)' : 'none' }} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
